import "dayjs/locale/id";
import {
  CloseOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Drawer,
  Empty,
  Flex,
  Tooltip,
  Typography,
  message,
} from "antd";
import { showConfirmModal } from "components/modals/confirmModal";
import { useDeleteScheduleDraft } from "hooks/mutations/schedule-draft";
import { getScheduleDraftsQuery } from "hooks/queries/schedule-draft";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ScheduleDraft } from "shared/types";
import { localTzDate, tzDate } from "shared/utils/dayjs";
import {
  applyCreateScheduleResumeDraft,
  parseScheduleDraftPayload,
} from "shared/utils/scheduleResumeDraft";
import { Dayjs } from "dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// Quiet 30px square icon action in the drawer header (NotificationDrawer's
// .icon-btn treatment).
const headerIconButtonStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  color: colors.ink3,
};

// What a list row shows about the schedule being drafted, derived from the
// stored payload — the row never trusts a separate copy of these facts.
const draftedForStamp = (row: ScheduleDraft): string => {
  const parsed = parseScheduleDraftPayload(row.payload);
  if (!parsed) return translate("schedule.drafts.no_date");
  const { formValues } = applyCreateScheduleResumeDraft(parsed);
  const date = formValues.schedule_date as Dayjs | undefined;
  const time = formValues.start_time as Dayjs | undefined;
  if (!date?.isValid?.()) return translate("schedule.drafts.no_date");
  const dateLabel = localTzDate(date.toISOString()).format("ddd, DD MMM");
  return time?.isValid?.()
    ? `${dateLabel} · ${tzDate(time.toISOString()).format("HH:mm")}`
    : dateLabel;
};

const DraftRow = ({
  row,
  onResume,
  onDelete,
}: {
  row: ScheduleDraft;
  onResume: (row: ScheduleDraft) => void;
  onDelete: (row: ScheduleDraft) => void;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <Flex
      role="button"
      tabIndex={0}
      onClick={() => onResume(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onResume(row);
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      align="flex-start"
      gap={8}
      style={{
        padding: 13,
        borderRadius: 14,
        cursor: "pointer",
        border: `1px solid ${colors.line}`,
        background: hover ? colors.bgSoft : colors.card,
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      <Flex vertical style={{ flex: 1, minWidth: 0 }} gap={2}>
        <Text
          strong
          style={{ fontSize: textPx(13.5), lineHeight: 1.3, wordBreak: "break-word" }}
        >
          {row.label ?? translate("schedule.drafts.untitled")}
        </Text>
        <Text style={{ fontSize: textPx(12), color: colors.ink2 }}>
          {draftedForStamp(row)}
        </Text>
        <Text style={{ fontSize: textPx(11), color: colors.ink3 }}>
          {translate("schedule.drafts.updated_prefix")}{" "}
          {localTzDate(row.updated_at).format("DD MMM HH:mm")}
        </Text>
      </Flex>
      <Button
        type="text"
        size="small"
        icon={<DeleteOutlined />}
        aria-label={translate("button.delete")}
        style={{ color: colors.ink3, flexShrink: 0 }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(row);
        }}
      />
    </Flex>
  );
};

// Trigger + drawer listing the acting admin's saved create-schedule drafts
// (NotificationDrawer skeleton). Resuming always navigates to the calendar
// page with the draft in router state, which the existing schedule/index.tsx
// consume effect turns into an open, prefilled panel.
/**
 * Opens the admin's saved drafts. `compact` is the icon-only form beside the
 * sider's *Jadwal Baru* button (the full label doesn't fit there in English);
 * the Daftar header keeps the labelled one. The drawer opens on its button's
 * side: from the left in the sider, where a resumed draft's create panel
 * opens too, and from the right in the Daftar header.
 */
export const ScheduleDraftsButton = ({
  compact = false,
}: {
  compact?: boolean;
}) => {
  const [open, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: drafts } = getScheduleDraftsQuery();
  const { mutate: deleteDraft } = useDeleteScheduleDraft();
  const rows = drafts ?? [];

  const handleResume = (row: ScheduleDraft) => {
    const parsed = parseScheduleDraftPayload(row.payload);
    if (!parsed) {
      message.error(translate("schedule.drafts.unreadable"));
      return;
    }
    setIsOpen(false);
    // Already on the calendar page → navigate to self (keeping ?date=); the
    // consume effect re-runs off location.state. Anywhere else (the Daftar
    // view) → land on the calendar fresh.
    const target =
      location.pathname === PATHS.SCHEDULE
        ? location.pathname + location.search
        : PATHS.SCHEDULE;
    navigate(target, {
      state: {
        scheduleDraft: { ...parsed, savedDraftId: row.id, pristine: true },
      },
    });
  };

  const handleDelete = (row: ScheduleDraft) => {
    showConfirmModal({
      tone: "error",
      destructive: true,
      title: translate("schedule.drafts.delete_confirm.title"),
      content: translate("schedule.drafts.delete_confirm.body"),
      confirmLabel: translate("button.delete"),
      onConfirm: () => deleteDraft(row.id),
    });
  };

  return (
    <>
      {/* offset pulls the count inside the button bounds — the toolbar sits in
          a clipping ancestor that cuts anything overhanging the top edge
          (NotificationDrawer's bell uses the same nudge for the same reason). */}
      <Badge
        count={rows.length}
        color="red"
        overflowCount={99}
        size="small"
        offset={[-4, 5]}
      >
        {compact ? (
          <Tooltip title={translate("schedule.drafts.title")}>
            <Button
              icon={<FileTextOutlined />}
              aria-label={translate("schedule.drafts.title")}
              onClick={() => setIsOpen(true)}
              style={{ width: 40, height: 40 }}
            />
          </Tooltip>
        ) : (
          <Button icon={<FileTextOutlined />} onClick={() => setIsOpen(true)}>
            {translate("schedule.drafts.button")}
          </Button>
        )}
      </Badge>
      <Drawer
        open={open}
        placement={compact ? "left" : "right"}
        onClose={() => setIsOpen(false)}
        closable={false}
        title={
          <Flex align="center" gap={8}>
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 600,
                fontSize: textPx(18),
                letterSpacing: "-0.01em",
              }}
            >
              {translate("schedule.drafts.title")}
            </span>
            {rows.length > 0 && (
              <span
                style={{
                  fontSize: textPx(11),
                  fontWeight: 600,
                  background: colors.navyTint,
                  color: colors.navyDark,
                  padding: "1px 8px",
                  borderRadius: 999,
                }}
              >
                {rows.length}
              </span>
            )}
          </Flex>
        }
        extra={
          <Button
            type="text"
            icon={<CloseOutlined />}
            style={headerIconButtonStyle}
            onClick={() => setIsOpen(false)}
          />
        }
        width={400}
        styles={{
          body: { padding: 0, display: "flex", flexDirection: "column" },
        }}
      >
        <div style={{ flex: 1, overflow: "auto", padding: "12px 14px 16px" }}>
          {rows.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text style={{ color: colors.ink3 }}>
                  {translate("schedule.drafts.empty")}
                </Text>
              }
              style={{ marginTop: 48 }}
            />
          ) : (
            <Flex gap={6} vertical>
              {rows.map((row) => (
                <DraftRow
                  key={row.id}
                  row={row}
                  onResume={handleResume}
                  onDelete={handleDelete}
                />
              ))}
            </Flex>
          )}
        </div>
      </Drawer>
    </>
  );
};
